import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddLocationTypeApplicationErrors,
  AddLocationTypeResponse,
  AddTypeToLocationApplicationErrors,
  AddTypeToLocationResponse,
  ArchiveLocationApplicationErrors,
  ArchiveLocationResponse,
  BulkCreateLocationResponse,
  CreateLocationApplicationErrors,
  ListLocationsOptions,
  ListLocationsResponse,
  Location,
  LocationArchiveStatusEnvelope,
  LocationCreatedEnvelope,
  LocationQuery,
  LocationSetDefaultLocationEnvelope,
  LocationTypeWithLiterals,
  LocationUpdatedEnvelope,
  LocationsQueryBuilder,
  QueryLocationsOptions,
  QueryLocationsResponse,
  RemoveLocationTypeApplicationErrors,
  RemoveLocationTypeResponse,
  RemoveTypeFromLocationApplicationErrors,
  RemoveTypeFromLocationResponse,
  SetDefaultLocationApplicationErrors,
  SetDefaultLocationResponse,
  UpdateLocation,
  UpdateLocationApplicationErrors,
  addLocationType as universalAddLocationType,
  addTypeToLocation as universalAddTypeToLocation,
  archiveLocation as universalArchiveLocation,
  bulkCreateLocation as universalBulkCreateLocation,
  createLocation as universalCreateLocation,
  getLocation as universalGetLocation,
  listLocations as universalListLocations,
  queryLocations as universalQueryLocations,
  removeLocationType as universalRemoveLocationType,
  removeTypeFromLocation as universalRemoveTypeFromLocation,
  setDefaultLocation as universalSetDefaultLocation,
  typedQueryLocations as universalTypedQueryLocations,
  updateLocation as universalUpdateLocation,
} from './locations-v1-location-locations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/business-tools' };

export function createLocation(
  httpClient: HttpClient
): CreateLocationSignature {
  return (
    location: NonNullablePaths<Location, `address` | `name` | `timeZone`, 2>
  ) =>
    universalCreateLocation(
      location,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateLocationSignature {
  /**
   * Creates a location.
   * @param - Location to create.
   * @returns Created location.
   */
  (
    location: NonNullablePaths<Location, `address` | `name` | `timeZone`, 2>
  ): Promise<
    NonNullablePaths<
      Location,
      | `name`
      | `default`
      | `status`
      | `locationType`
      | `address.streetAddress.number`
      | `address.streetAddress.name`
      | `address.streetAddress.apt`
      | `businessSchedule.periods`
      | `businessSchedule.periods.${number}.openDay`
      | `businessSchedule.periods.${number}.openTime`
      | `businessSchedule.periods.${number}.closeDay`
      | `businessSchedule.periods.${number}.closeTime`
      | `businessSchedule.specialHourPeriod`
      | `businessSchedule.specialHourPeriod.${number}.startDate`
      | `businessSchedule.specialHourPeriod.${number}.endDate`
      | `businessSchedule.specialHourPeriod.${number}.isClosed`
      | `businessSchedule.specialHourPeriod.${number}.comment`
      | `archived`
      | `locationTypes`,
      5
    > & {
      __applicationErrorsType?: CreateLocationApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkCreateLocation(
  httpClient: HttpClient
): BulkCreateLocationSignature {
  return (locations: Location[]) =>
    universalBulkCreateLocation(
      locations,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateLocationSignature {
  /**
   * Creates locations in bulk.
   * @param - Locations to create.
   */
  (locations: Location[]): Promise<
    NonNullablePaths<
      BulkCreateLocationResponse,
      | `successfulLocations`
      | `successfulLocations.${number}.name`
      | `successfulLocations.${number}.default`
      | `successfulLocations.${number}.status`
      | `successfulLocations.${number}.locationType`
      | `successfulLocations.${number}.address.streetAddress.number`
      | `successfulLocations.${number}.address.streetAddress.name`
      | `successfulLocations.${number}.address.streetAddress.apt`
      | `successfulLocations.${number}.archived`
      | `failedLocations`
      | `failedLocations.${number}.location.name`
      | `failedLocations.${number}.location.default`
      | `failedLocations.${number}.location.status`
      | `failedLocations.${number}.location.locationType`
      | `failedLocations.${number}.location.archived`
      | `failedLocations.${number}.errorMessage`,
      6
    >
  >;
}

export function getLocation(httpClient: HttpClient): GetLocationSignature {
  return (_id: string) =>
    universalGetLocation(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLocationSignature {
  /**
   * Retrieves a location.
   * @param - ID of the location to retrieve.
   * @returns Retrieved location.
   */
  (_id: string): Promise<
    NonNullablePaths<
      Location,
      | `name`
      | `default`
      | `status`
      | `locationType`
      | `address.streetAddress.number`
      | `address.streetAddress.name`
      | `address.streetAddress.apt`
      | `businessSchedule.periods`
      | `businessSchedule.periods.${number}.openDay`
      | `businessSchedule.periods.${number}.openTime`
      | `businessSchedule.periods.${number}.closeDay`
      | `businessSchedule.periods.${number}.closeTime`
      | `businessSchedule.specialHourPeriod`
      | `businessSchedule.specialHourPeriod.${number}.startDate`
      | `businessSchedule.specialHourPeriod.${number}.endDate`
      | `businessSchedule.specialHourPeriod.${number}.isClosed`
      | `businessSchedule.specialHourPeriod.${number}.comment`
      | `archived`
      | `locationTypes`,
      5
    >
  >;
}

export function listLocations(httpClient: HttpClient): ListLocationsSignature {
  return (options?: ListLocationsOptions) =>
    universalListLocations(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListLocationsSignature {
  /**
   * Retrieves locations, given the specified filters, sorting, and paging.
   * @param - Options to use when retrieving a list of locations.
   */
  (options?: ListLocationsOptions): Promise<
    NonNullablePaths<
      ListLocationsResponse,
      | `locations`
      | `locations.${number}.name`
      | `locations.${number}.default`
      | `locations.${number}.status`
      | `locations.${number}.locationType`
      | `locations.${number}.address.streetAddress.number`
      | `locations.${number}.address.streetAddress.name`
      | `locations.${number}.address.streetAddress.apt`
      | `locations.${number}.archived`
      | `authorizedLocationEntities`,
      6
    >
  >;
}

export function updateLocation(
  httpClient: HttpClient
): UpdateLocationSignature {
  return (
    _id: string,
    location: NonNullablePaths<
      UpdateLocation,
      `address` | `name` | `timeZone`,
      2
    >
  ) =>
    universalUpdateLocation(
      _id,
      location,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateLocationSignature {
  /**
   * Overrides an existing location.
   *
   *
   * > **Note:** Currently, it isn't possible to partially update a location. Therefore, you'll need to pass the full location object in the body of the call.
   * @param - Location ID.
   * @param - Updated location details.
   * @returns Updated location.
   */
  (
    _id: string,
    location: NonNullablePaths<
      UpdateLocation,
      `address` | `name` | `timeZone`,
      2
    >
  ): Promise<
    NonNullablePaths<
      Location,
      | `name`
      | `default`
      | `status`
      | `locationType`
      | `address.streetAddress.number`
      | `address.streetAddress.name`
      | `address.streetAddress.apt`
      | `businessSchedule.periods`
      | `businessSchedule.periods.${number}.openDay`
      | `businessSchedule.periods.${number}.openTime`
      | `businessSchedule.periods.${number}.closeDay`
      | `businessSchedule.periods.${number}.closeTime`
      | `businessSchedule.specialHourPeriod`
      | `businessSchedule.specialHourPeriod.${number}.startDate`
      | `businessSchedule.specialHourPeriod.${number}.endDate`
      | `businessSchedule.specialHourPeriod.${number}.isClosed`
      | `businessSchedule.specialHourPeriod.${number}.comment`
      | `archived`
      | `locationTypes`,
      5
    > & {
      __applicationErrorsType?: UpdateLocationApplicationErrors;
    }
  >;
}

export function queryLocations(
  httpClient: HttpClient
): QueryLocationsSignature {
  return (options?: QueryLocationsOptions) =>
    universalQueryLocations(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLocationsSignature {
  /**
   * Creates a query to retrieve a list of locations.
   *
   *
   * The `queryLocations()` function builds a query to retrieve a list of up to 1,000 locations and returns a `LocationsQueryBuilder` object.
   *
   * The returned object contains the query definition which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `LocationsQueryBuilder` functions onto the query. `LocationsQueryBuilder` functions enable you to sort, filter, and control the results that `queryLocations()` returns. The functions that are chained to `queryLocations()` are applied in the order they are called.
   *
   * `queryLocations()` runs with the following `LocationsQueryBuilder` defaults that you can override:
   * - `skip`: `0`
   * - `limit`: `50`
   *
   * The following `QueryLocationsBuilder` functions are supported for the `queryLocations()` function. For a full description of the Locations object, see the object returned for the `items` property in `LocationsQueryResult`.
   * @param - Options to use when querying locations.
   */
  (options?: QueryLocationsOptions): LocationsQueryBuilder;
}

export function typedQueryLocations(
  httpClient: HttpClient
): TypedQueryLocationsSignature {
  return (query: LocationQuery, options?: QueryLocationsOptions) =>
    universalTypedQueryLocations(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryLocationsSignature {
  /** */
  (query: LocationQuery, options?: QueryLocationsOptions): Promise<
    NonNullablePaths<
      QueryLocationsResponse,
      | `locations`
      | `locations.${number}.name`
      | `locations.${number}.default`
      | `locations.${number}.status`
      | `locations.${number}.locationType`
      | `locations.${number}.address.streetAddress.number`
      | `locations.${number}.address.streetAddress.name`
      | `locations.${number}.address.streetAddress.apt`
      | `locations.${number}.archived`
      | `authorizedLocationEntities`,
      6
    >
  >;
}

export function setDefaultLocation(
  httpClient: HttpClient
): SetDefaultLocationSignature {
  return (_id: string) =>
    universalSetDefaultLocation(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface SetDefaultLocationSignature {
  /**
   * Sets a new default location.
   *
   *
   * > **Notes:**
   * > + There can only be one default location per site.
   * > + The default location can't be archived.
   * @param - ID of the location to set as the default location.
   */
  (_id: string): Promise<
    NonNullablePaths<
      SetDefaultLocationResponse,
      | `location.name`
      | `location.default`
      | `location.status`
      | `location.locationType`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `location.address.streetAddress.apt`
      | `location.businessSchedule.periods`
      | `location.businessSchedule.periods.${number}.openDay`
      | `location.businessSchedule.periods.${number}.openTime`
      | `location.businessSchedule.periods.${number}.closeDay`
      | `location.businessSchedule.periods.${number}.closeTime`
      | `location.businessSchedule.specialHourPeriod`
      | `location.businessSchedule.specialHourPeriod.${number}.startDate`
      | `location.businessSchedule.specialHourPeriod.${number}.endDate`
      | `location.businessSchedule.specialHourPeriod.${number}.isClosed`
      | `location.businessSchedule.specialHourPeriod.${number}.comment`
      | `location.archived`
      | `location.locationTypes`,
      6
    > & {
      __applicationErrorsType?: SetDefaultLocationApplicationErrors;
    }
  >;
}

export function archiveLocation(
  httpClient: HttpClient
): ArchiveLocationSignature {
  return (_id: string) =>
    universalArchiveLocation(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchiveLocationSignature {
  /**
   * Archives a location.
   *
   *
   * > **Notes:**
   * > + Changes the `archived` boolean of a location to `true`.
   * > + You can't change a location's `status` using this endpoint.
   * > + Archived locations can't be updated.
   * > + The `default` location can't be archived.
   * @param - ID of the location to archive.
   */
  (_id: string): Promise<
    NonNullablePaths<
      ArchiveLocationResponse,
      | `location.name`
      | `location.default`
      | `location.status`
      | `location.locationType`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `location.address.streetAddress.apt`
      | `location.businessSchedule.periods`
      | `location.businessSchedule.periods.${number}.openDay`
      | `location.businessSchedule.periods.${number}.openTime`
      | `location.businessSchedule.periods.${number}.closeDay`
      | `location.businessSchedule.periods.${number}.closeTime`
      | `location.businessSchedule.specialHourPeriod`
      | `location.businessSchedule.specialHourPeriod.${number}.startDate`
      | `location.businessSchedule.specialHourPeriod.${number}.endDate`
      | `location.businessSchedule.specialHourPeriod.${number}.isClosed`
      | `location.businessSchedule.specialHourPeriod.${number}.comment`
      | `location.archived`
      | `location.locationTypes`,
      6
    > & {
      __applicationErrorsType?: ArchiveLocationApplicationErrors;
    }
  >;
}

export function addTypeToLocation(
  httpClient: HttpClient
): AddTypeToLocationSignature {
  return (_id: string, locationType: LocationTypeWithLiterals) =>
    universalAddTypeToLocation(
      _id,
      locationType,
      // @ts-ignore
      { httpClient }
    );
}

interface AddTypeToLocationSignature {
  /**
   * Adds a location type to the specified location.
   * @param - ID of the location where the type will be added.
   * @param - Location type to add.
   */
  (_id: string, locationType: LocationTypeWithLiterals): Promise<
    NonNullablePaths<
      AddTypeToLocationResponse,
      | `location.name`
      | `location.default`
      | `location.status`
      | `location.locationType`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `location.address.streetAddress.apt`
      | `location.businessSchedule.periods`
      | `location.businessSchedule.periods.${number}.openDay`
      | `location.businessSchedule.periods.${number}.openTime`
      | `location.businessSchedule.periods.${number}.closeDay`
      | `location.businessSchedule.periods.${number}.closeTime`
      | `location.businessSchedule.specialHourPeriod`
      | `location.businessSchedule.specialHourPeriod.${number}.startDate`
      | `location.businessSchedule.specialHourPeriod.${number}.endDate`
      | `location.businessSchedule.specialHourPeriod.${number}.isClosed`
      | `location.businessSchedule.specialHourPeriod.${number}.comment`
      | `location.archived`
      | `location.locationTypes`,
      6
    > & {
      __applicationErrorsType?: AddTypeToLocationApplicationErrors;
    }
  >;
}

export function removeTypeFromLocation(
  httpClient: HttpClient
): RemoveTypeFromLocationSignature {
  return (_id: string, locationType: LocationTypeWithLiterals) =>
    universalRemoveTypeFromLocation(
      _id,
      locationType,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveTypeFromLocationSignature {
  /**
   * Removes a location type from the specified location.
   * @param - ID of the location where the type will be removed.
   * @param - Location type to remove.
   */
  (_id: string, locationType: LocationTypeWithLiterals): Promise<
    NonNullablePaths<
      RemoveTypeFromLocationResponse,
      | `location.name`
      | `location.default`
      | `location.status`
      | `location.locationType`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `location.address.streetAddress.apt`
      | `location.businessSchedule.periods`
      | `location.businessSchedule.periods.${number}.openDay`
      | `location.businessSchedule.periods.${number}.openTime`
      | `location.businessSchedule.periods.${number}.closeDay`
      | `location.businessSchedule.periods.${number}.closeTime`
      | `location.businessSchedule.specialHourPeriod`
      | `location.businessSchedule.specialHourPeriod.${number}.startDate`
      | `location.businessSchedule.specialHourPeriod.${number}.endDate`
      | `location.businessSchedule.specialHourPeriod.${number}.isClosed`
      | `location.businessSchedule.specialHourPeriod.${number}.comment`
      | `location.archived`
      | `location.locationTypes`,
      6
    > & {
      __applicationErrorsType?: RemoveTypeFromLocationApplicationErrors;
    }
  >;
}

export function addLocationType(
  httpClient: HttpClient
): AddLocationTypeSignature {
  return (_id: string, locationType: LocationTypeWithLiterals) =>
    universalAddLocationType(
      _id,
      locationType,
      // @ts-ignore
      { httpClient }
    );
}

interface AddLocationTypeSignature {
  /**
   * Add location type to a specific location.
   *
   * > **Notes:**
   * > + Add one the location type (UNKNOWN, BRANCH, OFFICES, RECEPTION, HEADQUARTERS, INVENTORY) to a specific location by id.
   * > + This EP is deprecated - Please use AddTypeFromLocation instead.
   * @param - Location id to add type
   * @deprecated
   */
  (_id: string, locationType: LocationTypeWithLiterals): Promise<
    NonNullablePaths<
      AddLocationTypeResponse,
      | `location.name`
      | `location.default`
      | `location.status`
      | `location.locationType`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `location.address.streetAddress.apt`
      | `location.businessSchedule.periods`
      | `location.businessSchedule.periods.${number}.openDay`
      | `location.businessSchedule.periods.${number}.openTime`
      | `location.businessSchedule.periods.${number}.closeDay`
      | `location.businessSchedule.periods.${number}.closeTime`
      | `location.businessSchedule.specialHourPeriod`
      | `location.businessSchedule.specialHourPeriod.${number}.startDate`
      | `location.businessSchedule.specialHourPeriod.${number}.endDate`
      | `location.businessSchedule.specialHourPeriod.${number}.isClosed`
      | `location.businessSchedule.specialHourPeriod.${number}.comment`
      | `location.archived`
      | `location.locationTypes`,
      6
    > & {
      __applicationErrorsType?: AddLocationTypeApplicationErrors;
    }
  >;
}

export function removeLocationType(
  httpClient: HttpClient
): RemoveLocationTypeSignature {
  return (_id: string, locationType: LocationTypeWithLiterals) =>
    universalRemoveLocationType(
      _id,
      locationType,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveLocationTypeSignature {
  /**
   * Remove location type from a specific location.
   *
   * > **Notes:**
   * > + Remove location type (UNKNOWN, BRANCH, OFFICES, RECEPTION, HEADQUARTERS, INVENTORY) from a specific location by id.
   * > + This EP is deprecated - Please use RemoveTypeFromLocation instead.
   * @param - Location id to add type
   * @deprecated
   */
  (_id: string, locationType: LocationTypeWithLiterals): Promise<
    NonNullablePaths<
      RemoveLocationTypeResponse,
      | `location.name`
      | `location.default`
      | `location.status`
      | `location.locationType`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `location.address.streetAddress.apt`
      | `location.businessSchedule.periods`
      | `location.businessSchedule.periods.${number}.openDay`
      | `location.businessSchedule.periods.${number}.openTime`
      | `location.businessSchedule.periods.${number}.closeDay`
      | `location.businessSchedule.periods.${number}.closeTime`
      | `location.businessSchedule.specialHourPeriod`
      | `location.businessSchedule.specialHourPeriod.${number}.startDate`
      | `location.businessSchedule.specialHourPeriod.${number}.endDate`
      | `location.businessSchedule.specialHourPeriod.${number}.isClosed`
      | `location.businessSchedule.specialHourPeriod.${number}.comment`
      | `location.archived`
      | `location.locationTypes`,
      6
    > & {
      __applicationErrorsType?: RemoveLocationTypeApplicationErrors;
    }
  >;
}

export const onLocationArchiveStatus = EventDefinition(
  'wix.locations.v1.location_archive_status',
  true,
  (event: LocationArchiveStatusEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<LocationArchiveStatusEnvelope>();
export const onLocationCreated = EventDefinition(
  'wix.locations.v1.location_created',
  true,
  (event: LocationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.address' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<LocationCreatedEnvelope>();
export const onLocationSetDefaultLocation = EventDefinition(
  'wix.locations.v1.location_set_default_location',
  true,
  (event: LocationSetDefaultLocationEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<LocationSetDefaultLocationEnvelope>();
export const onLocationUpdated = EventDefinition(
  'wix.locations.v1.location_updated',
  true,
  (event: LocationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.address' },
            { path: 'modifiedFields.address' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<LocationUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddLocationTypeRequest,
  AddLocationTypeResponse,
  AddTypeToLocationRequest,
  AddTypeToLocationResponse,
  Address,
  AddressLocation,
  ArchiveLocation,
  ArchiveLocationRequest,
  ArchiveLocationResponse,
  BaseEventMetadata,
  BulkCreateLocationRequest,
  BulkCreateLocationResponse,
  BulkUpdateLocationRequest,
  BulkUpdateLocationResponse,
  BusinessSchedule,
  CreateLocationRequest,
  CreateLocationResponse,
  DayOfWeek,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  FailedCreateLocation,
  FailedUpdateLocation,
  GetLocationRequest,
  GetLocationResponse,
  GetOrCreateDefaultLocationRequest,
  GetOrCreateDefaultLocationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListLocationsOptions,
  ListLocationsRequest,
  ListLocationsResponse,
  Location,
  LocationArchiveStatusEnvelope,
  LocationCreatedEnvelope,
  LocationQuerySpec,
  LocationSetDefaultLocationEnvelope,
  LocationStatus,
  LocationType,
  LocationUpdatedEnvelope,
  LocationsQueryBuilder,
  LocationsQueryResult,
  MessageEnvelope,
  MetasiteLocationsRequest,
  MetasiteLocationsResponse,
  Paging,
  PagingMetadata,
  Query,
  QueryLocationsOptions,
  QueryLocationsRequest,
  QueryLocationsResponse,
  RemoveLocationTypeRequest,
  RemoveLocationTypeResponse,
  RemoveTypeFromLocationRequest,
  RemoveTypeFromLocationResponse,
  RestoreInfo,
  SetDefaultLocation,
  SetDefaultLocationRequest,
  SetDefaultLocationResponse,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  StreetAddress,
  TimePeriod,
  UnarchiveLocationRequest,
  UnarchiveLocationResponse,
  UpdateLocation,
  UpdateLocationRequest,
  UpdateLocationResponse,
  WebhookIdentityType,
  utils,
} from './locations-v1-location-locations.universal.js';
